import React from 'react';
import styled from 'styled-components';
import { X, Crown, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  animation: slideUp 0.3s ease;
  
  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div`
  padding: 16px 20px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  
  .icon {
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    flex-shrink: 0;
  }
  
  h2 {
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #6b7280;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const Message = styled.div`
  margin-bottom: 20px;
  
  .title {
    font-size: 15px;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  
  .description {
    font-size: 13px;
    color: #6b7280;
    line-height: 1.6;
  }
`;

const FeatureList = styled.div`
  background: #f9fafb;
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 20px;
  
  .title {
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 10px;
  }
  
  .features {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .feature-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #4b5563;
    
    &::before {
      content: '✓';
      width: 18px;
      height: 18px;
      background: #10b981;
      color: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 11px;
      font-weight: 600;
      flex-shrink: 0;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
`;

const Button = styled.button`
  flex: 1;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  &.primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
    }
    
    &:active {
      transform: translateY(0);
    }
  }
  
  &.secondary {
    background: #f3f4f6;
    color: #374151;
    
    &:hover {
      background: #e5e7eb;
    }
  }
`;

const TrialLimitModal = ({ 
  isOpen, 
  onClose, 
  featureName = '该功能',
  onUpgrade,
  isWarning = false, // 是否为预警模式（剩余10秒）
  remainingSeconds = 0, // 剩余秒数（仅预警模式使用）
  customMessage = null, // 自定义消息（用于功能不可用场景）
  isDisabled = false // 是否为功能不可用模式（体验版用户直接不可用）
}) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      // 跳转到价格页面（升级会员页面）
      navigate('/app/pricing');
    }
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  // 预警模式：显示剩余时间提示
  if (isWarning) {
    return (
      <ModalOverlay onClick={handleClose}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <HeaderLeft>
              <h2>试用时间即将结束</h2>
            </HeaderLeft>
            <CloseButton onClick={handleClose}>
              <X size={18} />
            </CloseButton>
          </ModalHeader>
          
          <ModalBody>
            <Message>
              <div className="title">
                <AlertCircle size={20} color="#f59e0b" />
                温馨提示
              </div>
              <div className="description">
                您的体验版试用时间还剩 <strong style={{ color: '#ef4444', fontSize: '16px' }}>{remainingSeconds}</strong> 秒。
                {remainingSeconds <= 5 && ` 时间到达后，本次${featureName === '会议助手' ? '会议' : '面试'}将自动结束。`}
              </div>
            </Message>
            
            <FeatureList>
              <div className="title">升级会员享受更多权益</div>
              <div className="features">
                <div className="feature-item">解锁全部核心功能</div>
                <div className="feature-item">查看完整报告和分析</div>
                <div className="feature-item">优先技术支持</div>
                <div className="feature-item">更多高级功能</div>
              </div>
            </FeatureList>
            
            <ButtonGroup>
              <Button className="secondary" onClick={handleClose}>
                我知道了
              </Button>
              <Button className="primary" onClick={handleUpgrade}>
                立即升级
              </Button>
            </ButtonGroup>
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    );
  }

  // 功能不可用模式：体验版用户直接不可用
  if (isDisabled) {
    return (
      <ModalOverlay onClick={handleClose}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <HeaderLeft>
              <Crown size={24} style={{ color: '#f59e0b' }} />
              <h2>会员专享</h2>
            </HeaderLeft>
            <CloseButton onClick={handleClose}>
              <X size={18} />
            </CloseButton>
          </ModalHeader>
          
          <ModalBody>
            <Message>
              <div className="title">
                <AlertCircle size={20} color="#ef4444" />
                体验版用户无法使用该功能
              </div>
              <div className="description">
                {customMessage || `体验版用户无法使用${featureName}功能。请升级会员后使用，享受无限制访问所有高级功能。`}
              </div>
            </Message>
            
            <FeatureList>
              <div className="title">会员专享权益</div>
              <div className="features">
                <div className="feature-item">解锁全部核心功能</div>
                <div className="feature-item">查看完整报告和分析</div>
                <div className="feature-item">优先技术支持</div>
                <div className="feature-item">更多高级功能</div>
              </div>
            </FeatureList>
            
            <ButtonGroup>
              <Button className="secondary" onClick={handleClose}>
                稍后再说
              </Button>
              <Button className="primary" onClick={handleUpgrade}>
                立即升级
              </Button>
            </ButtonGroup>
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    );
  }

  // 最终限制模式：试用时间已用完
  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <HeaderLeft>
            <Crown size={24} style={{ color: '#f59e0b' }} />
            <h2>试用额度已用完</h2>
          </HeaderLeft>
          <CloseButton onClick={handleClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>
        
        <ModalBody>
          <Message>
            <div className="title">
              <AlertCircle size={20} color="#ef4444" />
              感谢您的体验
            </div>
            <div className="description">
              您今日的3分钟免费试用已结束。升级为会员即可继续使用，享受无限制访问所有高级功能。
            </div>
          </Message>
          
          <FeatureList>
            <div className="title">会员专享权益</div>
            <div className="features">
              <div className="feature-item">解锁全部核心功能</div>
              <div className="feature-item">查看完整报告和分析</div>
              <div className="feature-item">优先技术支持</div>
              <div className="feature-item">更多高级功能</div>
            </div>
          </FeatureList>
          
          <ButtonGroup>
            <Button className="secondary" onClick={handleClose}>
              稍后再说
            </Button>
            <Button className="primary" onClick={handleUpgrade}>
              立即升级
            </Button>
          </ButtonGroup>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default TrialLimitModal;
